/**
 * CustomDashboardTab — "My Custom" tab where users pick and arrange their own cards.
 * Supports both Data Cards and Health Cards with a category-grouped picker.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { targetAPI } from '../../lib/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import {
  Plus, X, Search, LayoutGrid, Heart, BarChart2,
  Target, DollarSign, TrendingUp, Trophy, AlertTriangle,
  Building2, Activity, Users, ShoppingCart, GripVertical
} from 'lucide-react';
import { toast } from 'sonner';

const ICONS = { Target, DollarSign, TrendingUp, Trophy, AlertTriangle, Building2, Users, BarChart2, Activity, ShoppingCart };

const COLLECTION_LABELS = {
  opportunities: { label: 'CRM / Opportunities', color: '#800000' },
  invoices: { label: 'Invoices', color: '#059669' },
  sales_orders: { label: 'Sales Orders', color: '#2563EB' },
  activities: { label: 'Activities', color: '#7C3AED' },
  activity_report: { label: 'Activity Reports', color: '#7C3AED' },
  accounts: { label: 'Accounts', color: '#0891B2' },
};

export function CustomDashboardTab({ year, onRenderBlocks }) {
  const [myCardIds, setMyCardIds] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState('data'); // 'data' or 'health'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load user's custom selection
  const loadCustom = useCallback(async () => {
    setLoading(true);
    try {
      const res = await targetAPI.getCustomDashboard();
      setMyCardIds(res.data.card_ids || []);
    } catch { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCustom(); }, [loadCustom]);

  // When card selection changes, render the dashboard (debounced)
  const renderTimeoutRef = React.useRef(null);
  useEffect(() => {
    if (loading) return;
    if (renderTimeoutRef.current) clearTimeout(renderTimeoutRef.current);
    renderTimeoutRef.current = setTimeout(() => renderDashboard(), 500);
    return () => { if (renderTimeoutRef.current) clearTimeout(renderTimeoutRef.current); };
  }, [myCardIds, year]); // eslint-disable-line

  const renderDashboard = async () => {
    if (myCardIds.length === 0) {
      onRenderBlocks?.([]);
      return;
    }
    try {
      const res = await targetAPI.renderCustomDashboard(year);
      onRenderBlocks?.(res.data.blocks || []);
    } catch {
      onRenderBlocks?.([]);
    }
  };

  const addCard = async (cardId) => {
    if (myCardIds.includes(cardId)) {
      toast.info('Card already added');
      return;
    }
    const updated = [...myCardIds, cardId];
    setSaving(true);
    try {
      await targetAPI.saveCustomDashboard({ card_ids: updated });
      setMyCardIds(updated);
      toast.success('Card added');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const removeCard = async (cardId) => {
    const updated = myCardIds.filter(id => id !== cardId);
    setSaving(true);
    try {
      await targetAPI.saveCustomDashboard({ card_ids: updated });
      setMyCardIds(updated);
      toast.success('Card removed');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="space-y-3"><Skeleton className="h-10 w-48" /><Skeleton className="h-24" /></div>;

  return (
    <>
      {/* Toolbar */}
      <div className="flex items-center gap-2" data-testid="custom-dashboard-toolbar">
        <Button
          size="sm"
          className="bg-[#800000] hover:bg-[#9a1919] text-white"
          onClick={() => { setPickerMode('data'); setPickerOpen(true); }}
          data-testid="add-data-card-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Add Data Card
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="border-rose-200 text-rose-700 hover:bg-rose-50"
          onClick={() => { setPickerMode('health'); setPickerOpen(true); }}
          data-testid="add-health-card-btn"
        >
          <Heart className="h-4 w-4 mr-1" /> Add Health Card
        </Button>
        {saving && <span className="text-xs text-muted-foreground animate-pulse">Saving...</span>}
        {myCardIds.length > 0 && (
          <Badge variant="outline" className="ml-auto text-xs" data-testid="custom-card-count">
            {myCardIds.length} cards selected
          </Badge>
        )}
      </div>

      {myCardIds.length === 0 && (
        <div className="text-center py-16 text-gray-400" data-testid="custom-empty-state">
          <LayoutGrid className="h-14 w-14 mx-auto mb-3 text-gray-200" />
          <h3 className="text-base font-semibold text-gray-500">Your custom dashboard is empty</h3>
          <p className="text-sm text-gray-400 mt-1 max-w-md mx-auto">
            Click "Add Data Card" or "Add Health Card" to build your personalized view
          </p>
        </div>
      )}

      {/* Card Picker Dialog */}
      <CardPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        mode={pickerMode}
        onModeChange={setPickerMode}
        selectedIds={myCardIds}
        onAdd={addCard}
        onRemove={removeCard}
      />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Card Picker Dialog
// ═══════════════════════════════════════════════════════════════════════════
function CardPickerDialog({ open, onClose, mode, onModeChange, selectedIds, onAdd, onRemove }) {
  const [available, setAvailable] = useState({ data_cards: {}, health_cards: [] });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    targetAPI.getAvailableCards()
      .then(r => setAvailable(r.data || { data_cards: {}, health_cards: [] }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const filterBySearch = (cards) => {
    if (!search) return cards;
    const q = search.toLowerCase();
    return cards.filter(c => c.name?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[80vh] flex flex-col p-0">
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="text-base font-semibold flex items-center gap-2">
            {mode === 'health' ? <Heart className="h-4 w-4 text-rose-500" /> : <BarChart2 className="h-4 w-4 text-[#800000]" />}
            {mode === 'health' ? 'Add Health Card' : 'Add Data Card'}
          </DialogTitle>
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => onModeChange('data')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${mode === 'data' ? 'bg-[#800000] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid="picker-data-tab"
            >
              Data Cards
            </button>
            <button
              onClick={() => onModeChange('health')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${mode === 'health' ? 'bg-rose-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid="picker-health-tab"
            >
              Health Cards
            </button>
            <div className="flex-1" />
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search cards..."
                className="pl-8 h-8 text-xs w-48"
                data-testid="card-picker-search"
              />
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0">
          <div className="p-4 space-y-4">
            {loading ? (
              <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
            ) : mode === 'health' ? (
              <HealthCardList
                cards={filterBySearch(available.health_cards || [])}
                selectedIds={selectedIds}
                onAdd={onAdd}
                onRemove={onRemove}
              />
            ) : (
              <DataCardList
                cardsByCollection={available.data_cards || {}}
                search={search}
                selectedIds={selectedIds}
                onAdd={onAdd}
                onRemove={onRemove}
              />
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function HealthCardList({ cards, selectedIds, onAdd, onRemove }) {
  if (cards.length === 0) return <p className="text-sm text-gray-400 text-center py-6">No health cards found</p>;

  const HEALTH_DESCRIPTIONS = {
    so_pipeline: 'Multi-tab view of SO pipeline status, aging, and conversion rates',
    invoice_collection: 'Invoice aging analysis, collection rates, and payment tracking',
    deal_velocity: 'Average deal cycle time, stage durations, and bottleneck analysis',
    win_loss: 'Win/Loss pattern analysis, competitor insights, and deal factors',
    activity_cadence: 'Team activity patterns, cadence tracking, and engagement health',
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-2">Health Cards — Rich multi-tab analytics</p>
      {cards.map(c => {
        const isSelected = selectedIds.includes(c.id);
        return (
          <div
            key={c.id}
            className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${isSelected ? 'border-rose-200 bg-rose-50/50' : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'}`}
            data-testid={`health-card-pick-${c.id}`}
          >
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center shrink-0">
              <Heart className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800">{c.name}</p>
              <p className="text-[11px] text-gray-500 leading-tight mt-0.5">
                {HEALTH_DESCRIPTIONS[c.health_card_type] || c.description || 'Composite health analytics card'}
              </p>
            </div>
            {isSelected ? (
              <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-600 hover:bg-rose-100"
                onClick={() => onRemove(c.id)} data-testid={`remove-health-${c.id}`}>
                <X className="h-3 w-3 mr-1" /> Remove
              </Button>
            ) : (
              <Button size="sm" className="h-7 text-xs bg-rose-600 hover:bg-rose-700 text-white"
                onClick={() => onAdd(c.id)} data-testid={`add-health-${c.id}`}>
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DataCardList({ cardsByCollection, search, selectedIds, onAdd, onRemove }) {
  const collections = Object.entries(cardsByCollection);
  if (collections.length === 0) return <p className="text-sm text-gray-400 text-center py-6">No data cards found</p>;

  const q = search?.toLowerCase() || '';

  return (
    <div className="space-y-4">
      {collections.map(([coll, cards]) => {
        const filtered = q ? cards.filter(c => c.name?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q)) : cards;
        if (filtered.length === 0) return null;
        const meta = COLLECTION_LABELS[coll] || { label: coll, color: '#64748b' };

        return (
          <div key={coll}>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{meta.label}</span>
              <Badge variant="outline" className="text-[10px] h-4">{filtered.length}</Badge>
            </div>
            <div className="space-y-1">
              {filtered.map(c => {
                const isSelected = selectedIds.includes(c.id);
                const Icon = ICONS[c.icon] || Target;
                return (
                  <div
                    key={c.id}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border transition-all ${isSelected ? 'border-blue-200 bg-blue-50/50' : 'border-transparent hover:border-gray-200 hover:bg-gray-50'}`}
                    data-testid={`data-card-pick-${c.id}`}
                  >
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${c.color || meta.color}15` }}>
                      <Icon className="h-3.5 w-3.5" style={{ color: c.color || meta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-800 truncate">{c.name}</p>
                      <p className="text-[10px] text-gray-400">
                        {c.display_type} · {c.aggregation}{c.group_by ? ` by ${c.group_by}` : ''}
                      </p>
                    </div>
                    {isSelected ? (
                      <button onClick={() => onRemove(c.id)} className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50"
                        data-testid={`remove-data-${c.id}`}>
                        <X className="h-3 w-3" />
                      </button>
                    ) : (
                      <button onClick={() => onAdd(c.id)} className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50"
                        data-testid={`add-data-${c.id}`}>
                        <Plus className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
